import React, { useEffect, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, RefreshCw } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { displayToastError, displayToastSuccess } from '@/helpers';
import { SingleSelect } from '@/modules/common';
import {
  getCountriesData,
  getOrganization,
  getTimezonesData,
  OrganizationUpdateRequest,
  updateOrganization,
} from '@/modules/common/api';
import { CurrencySelector } from '@/modules/common/components/templates/organization-settings/components/currency-selector';
import { OrganizationLogoField } from '@/modules/common/components/templates/organization-settings/components/organization-logo-field';
import { ScheduleField } from '@/modules/common/components/templates/organization-settings/components/schedule/schedule-field';
import { ServicesSelector } from '@/modules/common/components/templates/organization-settings/components/services-selector';
import { applyOrganizationWorkSchedule } from '@/modules/common/components/templates/organization-settings/helpers/apply-organization-work-schedule';
import { parseWorkSchedule } from '@/modules/common/components/templates/organization-settings/helpers/parse-work-schedule';
import { useCurrencies } from '@/modules/common/hooks';
import {
  invalidateOrganizationQueries,
  invalidateTeamMemberRatesOnProjectQueries,
} from '@/modules/common/utils/query-invalidation';
import { invalidateGuideQueries } from '@/modules/guide/utils/guide-query-invalidation';
import {
  createOrganizationFormSchema,
  OrganizationFormValues,
  OrganizationIndustries,
} from '@/modules/onboarding/organization-setup';

interface OrganizationSettingsTemplateProps {
  onSuccess?: () => void;
  submitButtonText: string;
  wrapInCard?: boolean;
  title?: string;
  showSuccessToast?: boolean;
  onFormStateChange?: (isDirty: boolean, reset: () => void) => void;
  onSavingStateChange?: (isSaving: boolean) => void;
  onSubmitRefChange?: (submit: () => void) => void;
  renderBottomSection?: (props: {
    onSubmit: () => void;
    isLoading: boolean;
    isDisabled: boolean;
    submitButtonText: string;
    isDirty: boolean;
  }) => React.ReactNode;
}

export const OrganizationSettingsTemplate: React.FC<OrganizationSettingsTemplateProps> = ({
  onSuccess,
  submitButtonText,
  wrapInCard = false,
  title,
  showSuccessToast = true,
  onFormStateChange,
  onSavingStateChange,
  onSubmitRefChange,
  renderBottomSection,
}) => {
  const { t } = useTranslation(['common', 'authenticated.organization']);
  const queryClient = useQueryClient();
  const isInitialLoadCompleteRef = useRef(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [hasLogoError, setHasLogoError] = useState(false);

  const organizationFormSchema = createOrganizationFormSchema(t);

  const {
    data: organizationData,
    isLoading: isLoadingOrganization,
    isError: isOrganizationError,
    error: organizationError,
    refetch: refetchOrganization,
  } = useQuery({
    queryKey: ['organization'],
    queryFn: getOrganization,
  });

  const { currencies, isLoadingCurrencies } = useCurrencies();

  const { data: countriesData, isLoading: isLoadingCountries } = useQuery({
    queryKey: ['countries'],
    queryFn: getCountriesData,
  });

  const { data: timezonesData, isLoading: isLoadingTimezones } = useQuery({
    queryKey: ['timezones'],
    queryFn: getTimezonesData,
  });

  const updateOrganizationMutation = useMutation({
    mutationFn: updateOrganization,
    onSuccess: async () => {
      if (showSuccessToast) {
        displayToastSuccess(t('authenticated.organization:toast.changesSaved'));
      }

      // Re-baseline the form to the submitted values so isDirty becomes false.
      // We do NOT reset isInitialLoadCompleteRef — re-triggering the init cycle
      // would cause setValue calls from applyOrganizationWorkSchedule to race with
      // the query invalidation and leave the form dirty again.
      form.reset(form.getValues());

      // Invalidate queries after re-baselining so incoming fresh data doesn't
      // trigger the init useEffect (isInitialLoadCompleteRef.current stays true).
      await invalidateOrganizationQueries(queryClient);
      invalidateTeamMemberRatesOnProjectQueries(queryClient);
      await invalidateGuideQueries(queryClient);

      onSuccess?.();
    },
    onError: (error: Error) => {
      displayToastError(error.message);
    },
  });

  const form = useForm<OrganizationFormValues>({
    resolver: zodResolver(organizationFormSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      industry: null,
      country: null,
      timezone: null,
      defaultCurrencyCode: null,
      services: null,
    },
  });

  // Track if all async data has been loaded
  const isLoadingData =
    isLoadingOrganization || isLoadingCurrencies || isLoadingCountries || isLoadingTimezones;

  // Update form with organization data when all data is loaded
  useEffect(() => {
    if (organizationData && !isLoadingData && !isInitialLoadCompleteRef.current) {
      isInitialLoadCompleteRef.current = true;

      // Build the complete values object including work schedule
      const workScheduleValues = form.getValues('workSchedule');
      applyOrganizationWorkSchedule(form, organizationData);
      const resolvedWorkSchedule = form.getValues('workSchedule');

      const values: OrganizationFormValues = {
        ...workScheduleValues,
        name: organizationData.name,
        industry: organizationData.industry || null,
        country: organizationData.country || null,
        timezone:
          organizationData.timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone.replace('Europe/Kiev', 'Europe/Kyiv'),
        defaultCurrencyCode: organizationData.currency?.code || null,
        services: organizationData.services || null,
        workSchedule: resolvedWorkSchedule,
      };

      // Delay to let async sub-components (e.g. logo) settle before baselining.
      // form.reset() sets current values and defaultValues atomically — no dirty window.
      setTimeout(() => {
        form.reset(values);
        setIsInitialLoadComplete(true);
      }, 100);
    }
  }, [organizationData, isLoadingData, form]);

  // Reset isInitialLoadComplete when component unmounts
  useEffect(() => {
    return () => {
      isInitialLoadCompleteRef.current = false;
      setIsInitialLoadComplete(false);
    };
  }, []);

  // Notify parent of form state changes only after initial load is complete
  useEffect(() => {
    if (onFormStateChange && isInitialLoadComplete) {
      const isDirty = form.formState.isDirty;
      const isValid = form.formState.isValid;
      const canSave = isDirty && isValid;

      onFormStateChange(canSave, form.reset);
    }
  }, [
    form.formState.isDirty,
    form.formState.isValid,
    form.reset,
    onFormStateChange,
    isInitialLoadComplete,
  ]);

  // Notify parent of saving state changes
  useEffect(() => {
    if (onSavingStateChange) {
      onSavingStateChange(updateOrganizationMutation.isPending);
    }
  }, [updateOrganizationMutation.isPending, onSavingStateChange]);

  // Provide submit function to parent
  useEffect(() => {
    if (onSubmitRefChange) {
      onSubmitRefChange(() => form.handleSubmit(handleSubmit)());
    }
  }, [onSubmitRefChange, form]);

  const handleSubmit = (values: OrganizationFormValues) => {
    const workSchedule = parseWorkSchedule(values);

    const dataToSubmit = new OrganizationUpdateRequest(
      values.name,
      values.industry === '' ? null : values.industry,
      values.country === '' ? null : values.country,
      values.timezone === '' ? null : values.timezone,
      values.defaultCurrencyCode === '' ? null : values.defaultCurrencyCode,
      Array.isArray(values.services) && values.services.length === 0 ? null : values.services,
      workSchedule
    );

    updateOrganizationMutation.mutate(dataToSubmit);
  };

  const isLoading =
    isLoadingOrganization || isLoadingCurrencies || updateOrganizationMutation.isPending;

  const hasBlockingError = hasLogoError;

  const formContent = (
    <>
      {isOrganizationError && (
        <Alert variant="destructive" className="mb-6">
          <div className="flex items-center justify-between">
            <AlertDescription>{organizationError.message}</AlertDescription>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchOrganization()}
              className="ml-4 flex items-center"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('common:retry')}
            </Button>
          </div>
        </Alert>
      )}

      <div className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('authenticated.organization:fields.name')}</FormLabel>
              <FormControl>
                <Input placeholder={t('authenticated.organization:placeholders.name')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <OrganizationLogoField onValidationErrorChange={setHasLogoError} />

        <div className="flex flex-col gap-3 md:flex-row">
          <div className="min-w-0 flex-1 basis-0">
            <SingleSelect
              name="industry"
              control={form.control}
              options={OrganizationIndustries.map(industry => ({
                value: industry,
                label: t(`authenticated.organization:industries.${industry}`),
              }))}
              label={t('authenticated.organization:fields.industry')}
              placeholder={t('authenticated.organization:placeholders.industry')}
              searchable={false}
              isClearSelectionEnabled={false}
            />
          </div>

          <ServicesSelector control={form.control} />
        </div>

        <div className="flex flex-col gap-3 md:flex-row">
          <div className="min-w-0 flex-1 basis-0">
            <SingleSelect
              name="country"
              control={form.control}
              className="h-10 font-normal"
              options={(countriesData || []).map(country => ({
                value: country.name,
                label: country.name,
                customContent: (
                  <div className="flex w-full items-center gap-3">
                    <span className="text-lg">{country.flag}</span>
                    <span className="flex-1">{country.name}</span>
                  </div>
                ),
                searchValue: country.name,
              }))}
              label={t('authenticated.organization:fields.country')}
              placeholder={t('authenticated.organization:placeholders.country')}
              emptyMessage={t('authenticated.organization:placeholders.noCountriesFound')}
              disabled={isLoadingCountries}
              loading={isLoadingCountries}
              searchable={true}
              isClearSelectionEnabled={false}
              renderTrigger={(selectedOption, placeholderText) => {
                if (selectedOption) {
                  const selectedCountry = countriesData?.find(c => c.name === selectedOption.value);
                  return (
                    <div className="flex w-full items-center justify-start gap-2">
                      <span className="flex-shrink-0 text-lg">{selectedCountry?.flag}</span>
                      <span className="truncate text-left">{selectedOption.label}</span>
                      <ChevronDown className="ml-auto h-4 w-4 flex-shrink-0 opacity-50" />
                    </div>
                  );
                }
                return (
                  <div className="flex w-full items-center justify-between">
                    <span className="text-left font-normal text-muted-foreground">
                      {isLoadingCountries ? 'Loading...' : placeholderText}
                    </span>
                    <ChevronDown className="h-4 w-4 flex-shrink-0 opacity-50" />
                  </div>
                );
              }}
            />
          </div>

          <div className="min-w-0 flex-1 basis-0">
            <CurrencySelector
              control={form.control}
              currencies={currencies ?? []}
              isLoading={isLoadingCurrencies}
            />
          </div>
        </div>

        <SingleSelect
          name="timezone"
          control={form.control}
          options={(timezonesData || []).map(timezone => ({
            value: timezone.value,
            label: `${timezone.name} (${timezone.offset})`,
            searchValue: `${timezone.name} ${timezone.abbr} ${timezone.offset}`,
          }))}
          label={t('authenticated.organization:fields.timezone')}
          placeholder={t('authenticated.organization:placeholders.timezone')}
          emptyMessage={t('authenticated.organization:placeholders.noTimezonesFound')}
          disabled={isLoadingTimezones}
          loading={isLoadingTimezones}
          searchable={true}
          isClearSelectionEnabled={false}
        />

        <ScheduleField control={form.control} />
      </div>
    </>
  );

  const wrappedContent = wrapInCard ? (
    <Card>
      <CardContent className="pt-6">
        {title && <h3 className="mb-6 text-lg font-semibold">{title}</h3>}
        {formContent}
      </CardContent>
    </Card>
  ) : (
    formContent
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        {wrappedContent}
        {renderBottomSection &&
          renderBottomSection({
            onSubmit: form.handleSubmit(handleSubmit),
            isLoading: updateOrganizationMutation.isPending,
            isDisabled: isLoading || hasBlockingError || !form.formState.isValid,
            submitButtonText,
            isDirty: form.formState.isDirty,
          })}
      </form>
    </Form>
  );
};
